/**
 * Base Error object for all errors across the Poolside app.
 * It extends base JavaScript Error by props by some useful props that can be used at the top level caller function of the call chain.
 */
export class PoolsideError extends Error {
    msgForUser?: string;
    msgForDev?: string;
    retriable?: boolean;

    /**
     * @param {string} [message] General error message for logging.
     * @param {string} [msgForUser] Error message that is safe to show to the user.
     * Must be short enough to put it into a small alert window.
     * @param {string} [msgForDev] Error message that could be useful to show for a developer.
     * Must be short enough to extend the msgForUser with this and put the combined text it into a small alert window.
     * If not set, `messsage` should be used for extending `msgForUser` instead.
     * @param {boolean} [retriable=false] Set to true for any error that is probably a temporary error e.g. a network timeout.
     * This case it is worth extending the error message preseted to the user by the words: "Please try again".
     */
    constructor(message: string, msgForUser?: string, msgForDev?: string, retriable?: boolean) {
        super(message);
        this.msgForUser = msgForUser;
        this.msgForDev = msgForDev;
        this.retriable = retriable;
    }
}

export class RpcError extends PoolsideError {
    isRemote: boolean;
    /** WARNING: the app puts the error prop of the JSON-RPC response here, instead of the full response. */
    resp: object;
    req: object;
    /** The full RPC response. It is not redundant because the App does not set the full response for the `resp` prop of RpcError object.
    */
    respFull: { [ key: string ]: any };
    isTimeouted = false;
    accessDenied = false;

    /**
     * @param {string} [message] General error message for logging.
     * - can be the same as the `error` prop or `error.message` of the RPC response
     * - or can be generated by the app e.g: "Request timed out"
     * @param {string} [msgForUser] Error message that is safe to show to the user.
     * Must be short enough to put it into a small alert window.
     * @param {boolean} [retriable=false] Set to true for any error that is probably a temporary error e.g. a network timeout.
     * This case it is worth extending the error message preseted to the user by the words: "Please try again".
     * @param {string} [msgForDev] Error message that could be useful to show for a developer.
     * Must be short enough to extend the msgForUser with this and put the combined text it into a small alert window.
     * If not set, `messsage` should be used for extending `msgForUser` instead.     
     * @param {Object} errObj Error object of the RPC response.
     * @param {Object} request Original request object.
     * @param {boolean} [isRemote=true] 
     * True: Server was reachable but responded by an error:
     *       either by setting the error prop of a JSONRPC response or by setting the http status code to 401.
     * False: Server was not reachable or responded by an unexpected error e.g. 500.
     * @param {boolean} [isTimeouted]
     * @param {boolean} [accessDenied]
     * @param {object} [respFull]  /** The full RPC response. It is not redundant because the App does not set the full response for the `resp` prop of RpcError object.
    */
    constructor (message: string, msgForUser: string, msgForDev: string, retriable = false, errObj: Error, request: object, isRemote = true, isTimeouted: boolean, accessDenied: boolean, respFull?: { [ key: string ]: any }) {
        super(message, msgForUser, msgForDev, retriable);
        this.isRemote = isRemote;
        this.resp = errObj;
        this.req = request;
        this.respFull = respFull;
        if (isTimeouted) this.isTimeouted = true;
        if (accessDenied) this.accessDenied = true;
    }
}

export class MissingCoreParamsError extends PoolsideError {
    constructor () {
        const message = "Missing argument: coreParams"
        super(message);
    }
}
